import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import { useAlmaAvatar } from '../../../hooks/useAlmaAvatar';
import { supabase } from '../../../lib/supabase';
import {
  AlmaNote,
  Avatar,
  Button,
  MatchReasonCard,
  PhotoGrid,
  PhotoLightbox,
  SectionDivider,
  Spinner,
} from '../../../components/design-system';
import { formatPlaces } from '../../../lib/places';
import { refundLine, type RefundNotice } from '../../../lib/refundNotice';
import type { HostProfile, Match, Profile } from '../../../types';

interface TripHostData {
  match: Match & { match_reasons: string[]; match_note: string | null };
  host: Profile;
  hostProfile: HostProfile;
  itineraryStatus: string | null;
  /** Cities THIS trip covers — not the host's primary city. */
  tripCities: string[];
}

/** Handbook pipeline states → status-row pill treatment (per the Host Profile screen). */
const HANDBOOK_STATUS: Record<string, { icon: string; label: string; iconColor: string; bg: string; border: string }> = {
  ready:       { icon: 'clipboard-list', label: 'Handbook ready',        iconColor: 'var(--success-fg)', bg: 'rgb(var(--success) / .11)',      border: 'rgb(var(--success) / .28)' },
  approval:    { icon: 'eye',            label: 'Waiting your approval', iconColor: 'var(--accent)',     bg: 'var(--accent-subtle)',           border: 'var(--accent-border)' },
  draft:       { icon: 'pencil',         label: 'Draft in progress',     iconColor: 'var(--text-dim)',   bg: 'rgb(var(--text-dim-rgb) / .10)', border: 'rgb(var(--text-dim-rgb) / .22)' },
  unavailable: { icon: 'clock-off',      label: 'Not yet started',       iconColor: 'var(--text-dim)',   bg: 'rgb(var(--text-dim-rgb) / .10)', border: 'rgb(var(--text-dim-rgb) / .22)' },
};

function handbookState(itineraryStatus: string | null): keyof typeof HANDBOOK_STATUS {
  if (itineraryStatus === 'approved') return 'ready';
  if (itineraryStatus === 'submitted') return 'approval';
  if (itineraryStatus === 'draft') return 'draft';
  return 'unavailable';
}

export function HostProfileView() {
  const { id: matchId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const almaAvatar = useAlmaAvatar();
  const [data, setData] = useState<TripHostData | null>(null);
  const [hostCities, setHostCities] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [refunds, setRefunds] = useState<RefundNotice[]>([]);

  // Refunds for this trip (this city's payment, in a trip with a host per city).
  useEffect(() => {
    if (!user || !matchId) return;
    void supabase
      .from('refund_notices')
      .select('id, amount_cents, currency, fully_refunded, created_at')
      .eq('match_id', matchId)
      .order('created_at', { ascending: true })
      .then(({ data: rows }) => setRefunds((rows as RefundNotice[] | null) ?? []));
  }, [user, matchId]);

  useEffect(() => {
    if (!user || !matchId) return;
    void (async () => {
      const { data: match } = await supabase
        .from('matches')
        .select('*')
        .eq('id', matchId)
        .eq('traveler_id', user.id)
        .maybeSingle();

      if (!match) { setLoading(false); return; }
      const m = match as Record<string, unknown>;

      const [{ data: hostProfile }, { data: hostUser }, { data: itin }, { data: hostDests }] = await Promise.all([
        supabase.from('host_profiles').select('*').eq('profile_id', m.host_id).maybeSingle(),
        supabase.from('profiles').select('*').eq('id', m.host_id).maybeSingle(),
        supabase.from('itineraries').select('status').eq('match_id', matchId).maybeSingle(),
        supabase.from('host_destinations').select('destinations(name)').eq('host_id', m.host_id as string),
      ]);

      // The trip's own destinations. Falling back to the host's primary city
      // showed "Lisbon" for a trip the traveler booked across two cities. In a
      // trip with a host per city, this host has the traveler in their city only.
      let tripCities: string[] = [];
      if (m.leg_id) {
        const { data: leg } = await supabase
          .from('trip_legs')
          .select('city')
          .eq('id', m.leg_id as string)
          .maybeSingle();
        const legCity = (leg as { city: string | null } | null)?.city;
        if (legCity) tripCities = [legCity];
      }
      if (tripCities.length === 0 && m.portrait_id) {
        const { data: pt } = await supabase
          .from('taste_portraits')
          .select('destinations')
          .eq('id', m.portrait_id as string)
          .maybeSingle();
        tripCities = (pt as { destinations: string[] | null } | null)?.destinations ?? [];
      }

      setHostCities(
        ((hostDests as { destinations: { name: string } | null }[] | null) ?? [])
          .map((r) => r.destinations?.name)
          .filter((n): n is string => !!n),
      );

      if (hostProfile && hostUser) {
        setData({
          match: m as unknown as TripHostData['match'],
          host: hostUser as Profile,
          hostProfile: hostProfile as HostProfile,
          itineraryStatus: (itin as { status: string } | null)?.status ?? null,
          tripCities,
        });
      }
      setLoading(false);
    })();
  }, [user, matchId]);

  if (loading) return <div className="flex justify-center py-16"><Spinner /></div>;
  if (!data) return (
    <div className="flex flex-col items-center justify-center py-16 gap-3 px-6 text-center">
      <p className="text-sm text-app-muted">Trip not found.</p>
    </div>
  );

  const { match, host, hostProfile } = data;
  const firstName = host.full_name?.split(' ')[0] ?? 'Your host';
  // Name every city on this trip, never just the first.
  const city = formatPlaces(data.tripCities) || (hostProfile.city ?? '');
  // Cities this host covers beyond the ones in the headline.
  const shownCities = data.tripCities.length ? data.tripCities : [city];
  const otherCities = hostCities.filter((c) => !shownCities.includes(c));
  const reasons: string[] = match.match_reasons ?? [];
  const moodboard: string[] = hostProfile.moodboard_images ?? [];
  const photos = moodboard.filter(Boolean);
  const specialties: string[] = hostProfile.specialties ?? [];
  const responseTime = hostProfile.response_time ?? '~1 day';
  const hs = HANDBOOK_STATUS[handbookState(data.itineraryStatus)];

  return (
    <div className="px-5 pt-7 pb-20 md:px-10 md:pt-12 md:pb-[88px]">
      <div className="max-w-[800px] mx-auto">
        {refunds.length > 0 && (
          <div
            role="status"
            className="rounded-2xl border border-app-border bg-app-card px-4 py-3"
            style={{ marginBottom: 20 }}
          >
            <div className="flex items-center gap-2 text-sm font-medium text-app-fg">
              <i className="ti ti-receipt-refund" style={{ color: 'var(--accent)' }} />
              Refund
            </div>
            <ul className="mt-1 flex flex-col gap-0.5">
              {refunds.map((r) => <li key={r.id} className="text-sm text-app-fg">{refundLine(r)}</li>)}
            </ul>
            <p className="text-xs text-app-muted mt-1">It goes back to the card you paid with and can take 5 to 10 business days to show on your statement.</p>
          </div>
        )}

        {/* Status row */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5" style={{ marginBottom: 32 }}>
          <span
            className="inline-flex items-center gap-1.5 rounded-full whitespace-nowrap"
            style={{ padding: '5px 12px', fontSize: 'var(--fs-caption)', background: hs.bg, color: 'var(--text)', border: `0.5px solid ${hs.border}` }}
          >
            <i className={`ti ti-${hs.icon}`} style={{ fontSize: 'calc(11px * var(--text-scale))', color: hs.iconColor }} />
            {hs.label}
          </span>
          <span className="text-app-muted text-caption">·</span>
          <span className="inline-flex items-center gap-[5px] text-caption text-app-muted">
            <i className="ti ti-clock text-[length:calc(11px*var(--text-scale))]" />Replies in {responseTime}
          </span>
        </div>

        {/* Hero */}
        <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-5" style={{ marginBottom: 10 }}>
          <Avatar name={host.full_name ?? 'Host'} src={host.avatar_url ?? undefined} size={72} className="sm:mt-2" />
          <div className="min-w-0">
            <h1
              className="font-serif text-app-fg"
              style={{ fontSize: 'clamp(30px, 8.5vw, 40px)', fontWeight: 400, lineHeight: 1.06, letterSpacing: '-.015em', textWrap: 'pretty' }}
            >
              {firstName} has you<br />in {city || '…'}
            </h1>
            {/* Other cities this host covers, when they operate in more than one. */}
            {otherCities.length > 0 && (
              <p className="text-app-muted text-xs mt-2">
                Also hosts in {otherCities.join(' · ')}
              </p>
            )}
          </div>
        </div>

        {/* Video | Alma note + reasons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch" style={{ marginTop: 44 }}>
          <div className="flex flex-col">
            {hostProfile.video_url ? (
              <div className="flex-1 rounded-2xl overflow-hidden bg-black border border-app-border min-h-[240px]">
                <video
                  src={hostProfile.video_url}
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-contain"
                />
              </div>
            ) : (
              <div
                className="flex-1 flex flex-col items-center justify-center gap-3 min-h-[240px]"
                style={{ border: '1px solid var(--line)', borderRadius: 16, background: 'var(--surface)' }}
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-subtle)' }}>
                  <i className="ti ti-player-play" style={{ color: 'var(--accent)', fontSize: 'calc(20px * var(--text-scale))' }} />
                </div>
                <div className="text-center px-4">
                  <div className="font-serif text-sm text-app-fg">Intro coming soon</div>
                  <div className="text-caption text-app-muted mt-1">{firstName} is preparing their video</div>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-6">
            {match.match_note && (
              <AlmaNote
                label={`Why Alma chose ${firstName}`}
                quote={match.match_note}
                avatarUrl={almaAvatar ?? undefined}
                avatar={almaAvatar ? undefined : <div className="w-[38px] h-[38px] rounded-full avatar-alma flex items-center justify-center font-serif text-subhead shrink-0">A</div>}
              />
            )}
            {reasons.length > 0 && (
              <div className="flex flex-col gap-2">
                {reasons.map((r) => (
                  <MatchReasonCard key={r} title={r} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* In host's words */}
        {(hostProfile.bio || specialties.length > 0) && (
          <div style={{ marginTop: 44 }}>
            <SectionDivider icon="quote" label={`In ${firstName}'s words`} style={{ marginBottom: 16 }} />
            {hostProfile.bio ? (
              <div className="flex gap-5 items-start">
                <div className="w-0.5 shrink-0 rounded-sm self-stretch" style={{ background: 'var(--line)' }} />
                <p className="font-serif text-app-fg" style={{ fontSize: 'calc(18px * var(--text-scale))', fontWeight: 400, lineHeight: 1.62, textWrap: 'pretty', whiteSpace: 'pre-wrap' }}>
                  {hostProfile.bio}
                </p>
              </div>
            ) : (
              <p className="text-sm text-app-muted italic">
                {firstName} hasn't written an intro yet — but the handbook speaks for itself.
              </p>
            )}
            {specialties.length > 0 && (
              <div className="flex flex-wrap gap-1.5" style={{ marginTop: 20 }}>
                {specialties.map((s) => (
                  <span key={s} className="text-app-fg-2" style={{ fontSize: 'var(--fs-caption2)', fontWeight: 500, background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 999, padding: '2px 8px' }}>
                    {s}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Feed */}
        <div style={{ marginTop: 44 }}>
          <PhotoGrid
            images={moodboard}
            count={9}
            columns={3}
            gap={3}
            radius={3}
            alt={`${firstName}'s photo`}
            onCellClick={(i) => { if (moodboard[i]) setLightboxIndex(moodboard.slice(0, i).filter(Boolean).length); }}
          />
        </div>

        {/* CTA */}
        <div className="flex flex-wrap items-center gap-4" style={{ marginTop: 52 }}>
          <Button
            variant="inverted"
            leadingIcon="message-circle"
            style={{ fontSize: 'var(--fs-footnote)', padding: '12px 24px' }}
            onClick={() => navigate(`/trip/${matchId}/chat`)}
          >
            Start chatting with {firstName}
          </Button>
          <Button
            variant="outline"
            leadingIcon="clipboard-list"
            style={{ fontSize: 'var(--fs-footnote)', padding: '11px 20px' }}
            onClick={() => navigate(`/trip/${matchId}/handbook`)}
          >
            View handbook
          </Button>
        </div>
      </div>

      {lightboxIndex !== null && (
        <PhotoLightbox
          images={photos}
          startIndex={lightboxIndex}
          alt={`${firstName}'s photo`}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}

export default HostProfileView;
